import {
  type DocumentNode,
  type MutationHookOptions,
  type OperationVariables,
  type QueryHookOptions,
  useMutation,
  useQuery,
} from "@apollo/client";
import {
  ContactListQuery,
  ContactListsQuery,
  MarketingContactsQuery,
} from "../graphql/queries";
import {
  CreateContactListMutation,
  DeleteContactListsMutation,
  DeleteMarketingContactsMutation,
  ImportMarketingContactsMutation,
  UpdateContactListMutation,
} from "../graphql/mutations";
import type {
  ContactListQueryType,
  ContactListQueryVariablesType,
  ContactListsQueryType,
  ContactListsQueryVariablesType,
  CreateContactListMutationType,
  CreateContactListMutationVariablesType,
  DeleteContactListsMutationType,
  DeleteContactListsMutationVariablesType,
  DeleteMarketingContactsMutationType,
  DeleteMarketingContactsMutationVariablesType,
  ImportMarketingContactsMutationType,
  ImportMarketingContactsMutationVariablesType,
  MarketingContactsQueryType,
  MarketingContactsQueryVariablesType,
  UpdateContactListMutationType,
  UpdateContactListMutationVariablesType,
} from "@/types/graphql/partner.generated";

export const useContactListsQuery = (
  options?: QueryHookOptions<
    ContactListsQueryType,
    ContactListsQueryVariablesType
  >
) =>
  useQuery<ContactListsQueryType, ContactListsQueryVariablesType>(
    ContactListsQuery,
    options
  );

export const useContactListQuery = (
  options?: QueryHookOptions<
    ContactListQueryType,
    ContactListQueryVariablesType
  >
) =>
  useQuery<ContactListQueryType, ContactListQueryVariablesType>(
    ContactListQuery,
    options
  );

export const useMarketingContactsQuery = (
  options?: QueryHookOptions<
    MarketingContactsQueryType,
    MarketingContactsQueryVariablesType
  >
) =>
  useQuery<MarketingContactsQueryType, MarketingContactsQueryVariablesType>(
    MarketingContactsQuery,
    options
  );

// Списки и контакты перечитываются после каждой мутации (по образцу
// useEmailTemplateMutations): импорт меняет счётчик листа и состав
// контактов сразу в трёх запросах.
const REFETCH = {
  refetchQueries: ["ContactLists", "ContactList", "MarketingContacts"],
};

const useRefetchingMutation = <TData, TVariables extends OperationVariables>(
  mutation: DocumentNode,
  options?: MutationHookOptions<TData, TVariables>
) => useMutation<TData, TVariables>(mutation, { ...REFETCH, ...options });

export const useCreateContactListMutation = (
  options?: MutationHookOptions<
    CreateContactListMutationType,
    CreateContactListMutationVariablesType
  >
) => useRefetchingMutation(CreateContactListMutation, options);

export const useUpdateContactListMutation = (
  options?: MutationHookOptions<
    UpdateContactListMutationType,
    UpdateContactListMutationVariablesType
  >
) => useRefetchingMutation(UpdateContactListMutation, options);

export const useDeleteContactListsMutation = (
  options?: MutationHookOptions<
    DeleteContactListsMutationType,
    DeleteContactListsMutationVariablesType
  >
) => useRefetchingMutation(DeleteContactListsMutation, options);

export const useDeleteMarketingContactsMutation = (
  options?: MutationHookOptions<
    DeleteMarketingContactsMutationType,
    DeleteMarketingContactsMutationVariablesType
  >
) => useRefetchingMutation(DeleteMarketingContactsMutation, options);

export const useImportMarketingContactsMutation = (
  options?: MutationHookOptions<
    ImportMarketingContactsMutationType,
    ImportMarketingContactsMutationVariablesType
  >
) => useRefetchingMutation(ImportMarketingContactsMutation, options);
